/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Link,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../helpers/hooks';
import { useLoginSlice } from './slice';
import { selectAuthError, selectAuthLoading } from './slice/selectors';
import { selectIsAuthenticated } from '../../layouts/MainLayout/slice/selectors';
import { ROUTE_PATHS } from '../../routes/constants';

export default function LoginPage() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { actions } = useLoginSlice();

  const loading = useAppSelector(selectAuthLoading);
  const error = useAppSelector(selectAuthError);
  const isAuthenticated = useAppSelector(selectIsAuthenticated);

  const [mode, setMode] = useState<'login' | 'register'>('login');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  useEffect(() => {
    if (isAuthenticated) {
      navigate(ROUTE_PATHS.PORTAL);
    }
  }, [isAuthenticated]);

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (mode === 'login') {
      dispatch(actions.loginRequest({ email, password }));
    } else {
      dispatch(actions.registerRequest({ name, email, password }));
    }
  };

  const toggleMode = () => {
    dispatch(actions.clearError());
    setMode((prev) => (prev === 'login' ? 'register' : 'login'));
  };

  return (
    <Card sx={{ width: '100%', borderRadius: 3 }}>
      <CardContent sx={{ p: 4 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
          {mode === 'login' ? 'Welcome back' : 'Create your account'}
        </Typography>
        <Typography variant="body2" sx={{ mb: 3 }}>
          {mode === 'login'
            ? 'Sign in to manage your translations.'
            : 'Start translating your apps in minutes.'}
        </Typography>

        <Box component="form" onSubmit={handleSubmit}>
          <Stack spacing={2}>
            {mode === 'register' && (
              <TextField
                label="Full name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                fullWidth
              />
            )}
            <TextField
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              fullWidth
            />
            <TextField
              label="Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              fullWidth
              helperText={mode === 'register' ? 'At least 8 characters' : ' '}
            />

            {error && (
              <Typography variant="body2" color="error">
                {error}
              </Typography>
            )}

            <Button
              type="submit"
              variant="contained"
              size="large"
              disabled={loading}
              startIcon={
                loading ? <CircularProgress size={18} color="inherit" /> : null
              }
            >
              {mode === 'login' ? 'Sign in' : 'Create account'}
            </Button>
          </Stack>
        </Box>

        <Typography variant="body2" sx={{ mt: 3, textAlign: 'center' }}>
          {mode === 'login'
            ? "Don't have an account? "
            : 'Already have an account? '}
          <Link
            component="button"
            type="button"
            onClick={toggleMode}
            sx={{ fontWeight: 600 }}
          >
            {mode === 'login' ? 'Sign up' : 'Sign in'}
          </Link>
        </Typography>
      </CardContent>
    </Card>
  );
}
